<template>
  <div class="filter-favorites">
    <div class="flex items-center justify-between mb-2">
      <h3 class="text-sm font-medium">{{ filter.displayName }}</h3>
      <button 
        v-if="showFavoritesOnly"
        @click="clearFilter"
        class="text-xs text-primary hover:underline"
      >
        Clear
      </button>
    </div>
    
    <button 
      @click="toggleShowFavoritesOnly"
      class="w-full flex items-center justify-between px-3 py-2 border rounded-md transition-colors"
      :class="showFavoritesOnly 
        ? 'border-primary bg-primary/10 text-primary' 
        : 'border-input bg-background hover:bg-accent/50'"
    >
      <div class="flex items-center gap-2">
        <HeartIcon class="h-4 w-4" :class="showFavoritesOnly ? 'text-primary' : 'text-muted-foreground'" />
        <span class="text-sm">Show favorites only</span>
      </div>
      <span v-if="favoritesCount > 0" class="text-xs bg-secondary px-2 py-0.5 rounded-full">
        {{ favoritesCount }}
      </span>
    </button>
    
    <!-- Add a message when there are no favorites -->
    <p v-if="showFavoritesOnly && favoritesCount === 0" class="text-sm text-muted-foreground mt-2">
      No favorites found. Add vehicles to your favorites to see them here.
    </p>
    
    <!-- Add a list of favorites when there are favorites -->
    <div v-if="favoritesCount > 0 && debugMode" class="mt-3 p-2 bg-muted/30 rounded-md text-xs">
      <p class="font-medium mb-1">Current favorites:</p>
      <ul class="list-disc pl-4">
        <li v-for="(favorite, index) in (vehiclesStore as any).favorites" 
            :key="index" 
            class="truncate"
            :title="favorite">
          {{ favorite }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { HeartIcon } from '@heroicons/vue/24/solid'
import { useVehiclesStore } from '~/stores/vehicles'
import { useRouter } from 'vue-router'

const props = defineProps({
  filter: {
    type: Object,
    required: true
  },
  activeValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:value', 'clear'])

const vehiclesStore = useVehiclesStore()
const debugMode = ref(false) // Set to true to see favorites list for debugging

// Ensure favorites is initialized as an array
onMounted(() => {
  if (!Array.isArray((vehiclesStore as any).favorites)) {
    console.warn('Favorites is not an array in FilterFavorites');
    (vehiclesStore as any).$patch({ favorites: [] })
  }
  
  // Force UI update if we have favorites
  if ((vehiclesStore as any).favorites.length > 0) {
    console.log(`FilterFavorites: Found ${(vehiclesStore as any).favorites.length} favorites on mount`)
  }
})

const showFavoritesOnly = computed(() => {
  return !!props.activeValue
})

const favoritesCount = computed(() => {
  // Ensure favorites is an array before getting length
  if (!Array.isArray((vehiclesStore as any).favorites)) {
    console.warn('Favorites is not an array in FilterFavorites computed')
    return 0
  }
  return (vehiclesStore as any).favorites.length
})

const router = useRouter()

const toggleShowFavoritesOnly = async () => {
  // Ensure favorites is initialized as an array
  if (!Array.isArray((vehiclesStore as any).favorites)) {
    (vehiclesStore as any).$patch({ favorites: [] })
  }

  if (!showFavoritesOnly.value) {
    // Simplified check - if there are any favorites, show them
    if ((vehiclesStore as any).favorites.length === 0) {
      console.log('No favorites found')
      return
    }
    
    console.log(`Enabling favorites filter with ${(vehiclesStore as any).favorites.length} favorites`)
    
    // Emit the event to update the filter value
    emit('update:value', true)
  } else {
    // If turning off favorites, emit the event
    emit('update:value', false)
  }
}

const clearFilter = () => {
  emit('clear')
}
</script>
